"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import Image from "next/image";
import { useRef } from "react";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import type { ServicePageContent } from "../../../types/types";
import { useServiceCardsAnimation } from "./use-service-cards-animation";
type ServicePageTemplateProps = {
  service: ServicePageContent;
};
type DeliverableCardProps = {
  card: Exclude<ServicePageContent["deliverables"], undefined>["cards"][number];
};
function DeliverableCard(t0) {
  const $ = _c(14);
  const {
    card
  } = t0;
  let t1;
  if ($[0] !== card.image) {
    t1 = <div className="relative h-full lg:max-h-50"><Image data-service-card-media={true} src={card.image} alt="" aria-hidden="true" className="h-full w-full object-cover rounded-2xl" /></div>;
    $[0] = card.image;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== card.title) {
    t2 = <h3 className="text-xl font-medium">{card.title}</h3>;
    $[2] = card.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== card.items) {
    t3 = card.items.map(_temp);
    $[4] = card.items;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <ul className="mt-6">{t3}</ul>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t4) {
    t5 = <div className="pt-9">{t2}{t4}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t5) {
    t6 = <article data-service-card={true}>{t1}{t5}</article>;
    $[11] = t1;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp(item) {
  return <li key={item} className="flex items-center gap-3 border-b border-gray-300 py-4 text-sm text-white/40"><Check strokeWidth={3} className="h-4.5 w-4.4 text-primary-300" /><span>{item}</span></li>;
}
export default function DeliverablesServicesSection(t0) {
  const $ = _c(31);
  const {
    service
  } = t0;
  const sectionRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      preset: "cascade"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useServiceCardsAnimation(sectionRef, t1);
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="hidden md:inline-flex absolute -top-1.75 -left-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />;
    t3 = <span className="hidden md:inline-flex absolute -top-1.75 -right-1.75 h-3 w-3 border border-gray-300 bg-gray-1000" />;
    t4 = <span className="hidden md:inline-flex absolute -bottom-1.75 -left-1.75 h-3 w-3 border border-gray-300 bg-gray-1000 z-40" />;
    t5 = <span className="hidden md:inline-flex absolute -bottom-1.75 -right-1.75 h-3 w-3 border border-gray-300 bg-gray-1000 z-40" />;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  const t6 = service.deliverables?.tag;
  let t7;
  if ($[5] !== t6) {
    t7 = <p className="tag">{t6}</p>;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const t8 = service.deliverables?.title;
  let t9;
  if ($[7] !== t8) {
    t9 = <h2 className="title whitespace-pre-line">{t8}</h2>;
    $[7] = t8;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t7 || $[10] !== t9) {
    t10 = <div className="flex flex-col gap-8 xl:flex-row xl:items-end xl:justify-between"><div className="max-w-160">{t7}{t9}</div></div>;
    $[9] = t7;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      enabled: true
    };
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = [Navigation, A11y];
    t13 = {
      prevEl: ".deliverables-prev",
      nextEl: ".deliverables-next"
    };
    $[13] = t12;
    $[14] = t13;
  } else {
    t12 = $[13];
    t13 = $[14];
  }
  let t14;
  if ($[15] !== service.deliverables?.cards) {
    t14 = service.deliverables?.cards.map(_temp2);
    $[15] = service.deliverables?.cards;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t14) {
    t15 = <Swiper a11y={t11} className="overflow-visible!" modules={t12} navigation={t13} slidesPerView="auto" spaceBetween={36}>{t14}</Swiper>;
    $[17] = t14;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <button aria-label="Card anterior" className="deliverables-prev cursor-pointer text-white transition-all duration-300 ease-in-out" type="button"><ArrowLeft className="size-9 text-primary-300" strokeWidth={1.5} /></button>;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="mt-6 flex items-center gap-5">{t16}<button aria-label={"Pr\xF3ximo card"} className="deliverables-next cursor-pointer text-white transition-all duration-300 ease-in-out" type="button"><ArrowRight className="size-9 text-primary-300" strokeWidth={1.5} /></button></div>;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  let t18;
  if ($[21] !== t15) {
    t18 = <div data-service-cards-animation-trigger={true} className="mt-12 lg:hidden">{t15}{t17}</div>;
    $[21] = t15;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== service.deliverables?.cards) {
    t19 = service.deliverables?.cards.map(_temp3);
    $[23] = service.deliverables?.cards;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  let t20;
  if ($[25] !== t19) {
    t20 = <div data-service-cards-animation-trigger={true} className="mt-12 hidden gap-10 lg:grid xl:grid-cols-3 xl:justify-between xl:gap-16">{t19}</div>;
    $[25] = t19;
    $[26] = t20;
  } else {
    t20 = $[26];
  }
  let t21;
  if ($[27] !== t10 || $[28] !== t18 || $[29] !== t20) {
    t21 = <section ref={sectionRef} className="md:container-padding relative border-y border-gray-300/80 text-white"><div className="container relative md:border-x md:border-gray-300 px-6 lg:px-18 3xl:px-24! py-18 lg:py-22">{t2}{t3}{t4}{t5}{t10}{t18}{t20}</div></section>;
    $[27] = t10;
    $[28] = t18;
    $[29] = t20;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  return t21;
}
function _temp3(card_0) {
  return <DeliverableCard key={card_0.title} card={card_0} />;
}
function _temp2(card) {
  return <SwiperSlide className="h-auto! max-w-[92%]" key={card.title}><DeliverableCard card={card} /></SwiperSlide>;
}